import { Dispatch, SetStateAction, useEffect } from 'react';

import { Box } from '@mui/material';
import { GridColDef, GridRowSelectionModel } from '@mui/x-data-grid-pro';
import { useFormikContext } from 'formik';

import { PanelGrid } from 'components/grid';
import { CondicaoPagamento } from 'pages/complementar/condicao-pagamento/types';
import { useQueryCondicoesPagamentoLoja } from 'pages/compra/configuracao-pedido/hooks';
import { ConfigPedidoCondicoesPagamento } from 'pages/compra/configuracao-pedido/types';

export type ConfigPedidoCondicoesPagamentoModalGrid = {
    id: number | null;
    open: boolean;
    selectedCondicoesPagamento: CondicaoPagamento[];
    setSelectedCondicoesPagamento: Dispatch<SetStateAction<CondicaoPagamento[]>>;
};

const columns: GridColDef[] = [
    { field: 'id', headerName: 'Código', width: 80 },
    { field: 'dsCondicaoPagamento', headerName: 'Descrição', flex: 1, minWidth: 100 },
    { field: 'nrParcela', headerName: 'Número de parcelas', width: 80 },
    { field: 'cdCondicaoPagamento', headerName: 'Código de integração', width: 100 },
];

/**
 * Retorna as linhas da grid.
 */
const getRows = (data: CondicaoPagamento[] = []) =>
    data.map((item) => ({
        id: item.idCondicaoPagamento,
        nrParcela: item.nrParcela,
        dsCondicaoPagamento: item.dsCondicaoPagamento,
        cdCondicaoPagamento: item.cdCondicaoPagamento,
    }));

export function ConfigPedidoCondicoesPagamentoModalGrid({
    id,
    open,
    selectedCondicoesPagamento,
    setSelectedCondicoesPagamento,
}: ConfigPedidoCondicoesPagamentoModalGrid) {
    const { values } = useFormikContext<ConfigPedidoCondicoesPagamento>();
    const { data, refetch, isFetching } = useQueryCondicoesPagamentoLoja({ configPedidoCondicoesPagamento: values });
    const rows = getRows(data?.data);

    useEffect(() => {
        if (open && !isFetching) {
            const lojaAtual = values.condicaoPagamentoLoja.find((loja) => loja.loja.cdLoja === id);

            if (lojaAtual) {
                setSelectedCondicoesPagamento(lojaAtual.condicaoPagamentoList);
            }
        }
    }, [open, isFetching]);

    const onClickRow = (selectionModel: GridRowSelectionModel) => {
        if (selectionModel.ids.size > selectedCondicoesPagamento.length) {
            const added = data?.data?.find((item) => item.idCondicaoPagamento === Array.from(selectionModel.ids).at(-1));

            if (added) {
                setSelectedCondicoesPagamento((prev) => [...prev, added]);
            }
        } else {
            setSelectedCondicoesPagamento((prev) => prev.filter((item) => selectionModel.ids.has(item.idCondicaoPagamento as number)));
        }
    };

    return (
        // Usamos o stopPropagation para evitar que ocorra erro ao clicar num campo da grid.
        <Box sx={{ height: 400 }} onClick={(e) => e.stopPropagation()}>
            <PanelGrid
                checkboxSelection
                autoHeight={false}
                rows={rows}
                rowsCount={rows.length}
                columns={columns}
                reload={refetch}
                loading={isFetching}
                onClickRow={onClickRow}
                selectionModel={{ type: 'include', ids: new Set(selectedCondicoesPagamento.map((item) => item.idCondicaoPagamento as number)) }}
            />
        </Box>
    );
}
